import axios from "axios";
import { useQuery } from "react-query";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { END_POINT } from "config/environment";

export type TSmartScreenContextTip = {
  id: number;
  title: string;
  subtitle?: string | null;
  icon?: string | null;
  icon_url?: string | null;
};

export const QUERY_KEY_SMART_SCREEN_CONTEXT_TIPS =
  "smart-screen-context-tips";

const FALLBACK_TIPS: TSmartScreenContextTip[] = [
  {
    id: -1,
    title:
      "Smart Screen automatically extracts key information from CVs such as skills, experience and education.",
    subtitle: "This saves you time and ensures consistent, unbiased screening.",
  },
  {
    id: -2,
    title:
      "CVs are automatically ranked based on how well they match your job requirements.",
    subtitle: "Get a shortlist of the most relevant candidates, faster.",
  },
  {
    id: -3,
    title:
      "Smart Screen helps reduce bias by focusing on skills and qualifications.",
    subtitle: "Every candidate is evaluated using the same criteria.",
  },
];

const getData = async (token: string): Promise<TSmartScreenContextTip[]> => {
  try {
    const url = `${END_POINT.BASE_URL}/v1/context-tips`;
    const res = await axios.get(url, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: { limit: 20 },
    });

    const tips = Array.isArray(res.data?.data) ? res.data.data : [];
    return tips.length > 0 ? tips : FALLBACK_TIPS;
  } catch {
    return FALLBACK_TIPS;
  }
};

export const useGetSmartScreenContextTips = (enabled = true) => {
  const { token } = useGetUserInfo();

  return useQuery(
    [QUERY_KEY_SMART_SCREEN_CONTEXT_TIPS],
    () => getData(token),
    {
      enabled: enabled && Boolean(token),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      placeholderData: FALLBACK_TIPS,
    },
  );
};
